import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Info } from 'lucide-react';
import { fetchYieldSheetsByDateRange, fetchYieldSheetList, fetchPurchaseOrderById, fetchYieldSheetsByVendorHistory } from '../services/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

function isoToday() {
  return new Date().toISOString().split('T')[0];
}

function isoMonthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function fmtDate(str) {
  if (!str) return '—';
  try {
    const d = new Date(str);
    if (isNaN(d)) return str;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return str; }
}

// MM/DD/YYYY format matching the live site
function fmtDateShort(str) {
  if (!str) return '—';
  try {
    const d = new Date(str);
    if (isNaN(d)) return str;
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
  } catch { return str; }
}

function fmtUnit(n, unit) {
  const v = Number(n);
  if (isNaN(v) || v === 0) return '—';
  const u = unit || 'BU';
  return `${fmtNum(v, 2)} ${u}`;
}

function fmtNum(n, decimals = 1) {
  const v = Number(n);
  if (isNaN(v)) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function fmtLbs(n) {
  const v = Number(n);
  if (isNaN(v) || v === 0) return '—';
  return `${fmtNum(v, 1)} lbs`;
}

function fmtPct(n) {
  const v = Number(n);
  if (isNaN(v)) return '—';
  return `${v.toFixed(1)}%`;
}

function fmtMoney(n) {
  const v = Number(n);
  if (isNaN(v)) return '$0.00';
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const WHOLESALE_LABELS = { 1: '#1s (Large Male)', 2: '#2s (Medium Male)', 3: '#3s (Female)' };

// ─── List View ────────────────────────────────────────────────────────────────

const PO_COLS = [
  'Crabber Name', 'Date Received', 'Date Processed',
  'Crab Cost', 'Transportation Cost',
  'Total Crabs', 'Total Yield', 'Total Yield %',
  'Male Retail Ratio', 'Female Retail Ratio', 'Total Retail Ratio', 'Total Retail $',
  'View',
];

function DateGroupRow({ group, onSelect, isExpanded, poList, poLoading, onOpenDetail }) {
  return (
    <>
      <tr
        style={{ borderBottom: '1px solid var(--gray-200)', cursor: 'pointer', backgroundColor: isExpanded ? '#f0f7f0' : 'white' }}
        onClick={onSelect}
      >
        <td style={{ padding: '9px 14px', fontWeight: 600 }}>{fmtDateShort(group.dateReceived)}</td>
        <td style={{ padding: '9px 14px' }}>{group.totalCount}</td>
        <td style={{ padding: '9px 14px' }}>{group.finalizeCount}</td>
        <td style={{ padding: '9px 14px', textAlign: 'right', color: 'var(--gray-400)' }}>
          {isExpanded ? '∧' : '∨'}
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={5} style={{ padding: 0, background: '#f8faf8' }}>
            {poLoading ? (
              <div style={{ padding: '12px 24px', fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>
            ) : poList && poList.length === 0 ? (
              <div style={{ padding: '12px 24px', fontSize: 12, color: 'var(--gray-500)' }}>No records for this date.</div>
            ) : poList ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#e8f0e8', borderBottom: '1px solid var(--gray-200)' }}>
                      {PO_COLS.map(h => (
                        <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, fontSize: 10, color: '#444', textTransform: 'none', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {poList.map((po, i) => (
                      <PoRow key={po.purchaseOrderId || i} po={po} alt={i % 2 === 1}
                        onSelect={() => onOpenDetail && onOpenDetail(po.purchaseOrderId, po.crabberName ?? po.CrabberName)} />
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </td>
        </tr>
      )}
    </>
  );
}

function PoRow({ po, alt, onSelect }) {
  const totalCrabs = Number(po.totalCrabs || 0);
  const totalYield = Number(po.totalYield || 0);
  const unit = po.unitsOfMeasure || 'BU';
  // For LBS vendors totalCrabs is weight (lbs); totalCrabCount = box+bushel count = bushel equivalent
  const bushelDenominator = Number(po.totalCrabCount || 0) || (unit === 'LBS' ? 0 : totalCrabs);
  const yieldPct = bushelDenominator > 0 ? (totalYield / bushelDenominator) * 100 : 0;
  const totalAmount = Number(po.totalAmount || 0) + Number(po.transportationCost || 0);
  const maleRetail = Number(po.totalMaleRetailPrice || 0);
  const femaleRetail = Number(po.totalFemaleRetailPrice || 0);
  // Retail Ratio = (retail / cost - 1) * 100 — cost includes transportation
  const maleRatio = totalAmount > 0 ? ((maleRetail / totalAmount) - 1) * 100 : 0;
  const femaleRatio = totalAmount > 0 ? ((femaleRetail / totalAmount) - 1) * 100 : 0;
  const totalRatio = totalAmount > 0 ? (((maleRetail + femaleRetail) / totalAmount) - 1) * 100 : 0;

  return (
    <tr
      style={{ borderBottom: '1px solid var(--gray-100)', cursor: 'pointer', background: alt ? '#f4f8f4' : 'white' }}
      onMouseEnter={e => e.currentTarget.style.background = '#e8f3e8'}
      onMouseLeave={e => e.currentTarget.style.background = alt ? '#f4f8f4' : 'white'}
    >
      <td style={{ padding: '7px 10px', fontWeight: 600 }}>{po.crabberName || '—'}</td>
      <td style={{ padding: '7px 10px' }}>{fmtDateShort(po.dateReceived)}</td>
      <td style={{ padding: '7px 10px' }}>{fmtDateShort(po.dateProcessed)}</td>
      <td style={{ padding: '7px 10px' }}>{fmtMoney(po.totalAmount)}</td>
      <td style={{ padding: '7px 10px' }}>{fmtMoney(po.transportationCost)}</td>
      <td style={{ padding: '7px 10px' }}>{totalCrabs > 0 ? `${fmtNum(totalCrabs, 0)} ${unit}` : '—'}</td>
      <td style={{ padding: '7px 10px' }}>{totalYield > 0 ? `${fmtNum(totalYield, 2)} Bushel(s)` : '—'}</td>
      <td style={{ padding: '7px 10px' }}>{totalCrabs > 0 ? fmtPct(yieldPct) : '—'}</td>
      <td style={{ padding: '7px 10px' }}>{maleRatio > 0 ? fmtPct(maleRatio) : ''}</td>
      <td style={{ padding: '7px 10px' }}>{femaleRatio > 0 ? fmtPct(femaleRatio) : ''}</td>
      <td style={{ padding: '7px 10px' }}>{totalRatio > 0 ? fmtPct(totalRatio) : ''}</td>
      <td style={{ padding: '7px 10px', fontWeight: 600 }}>{(maleRetail + femaleRetail) > 0 ? fmtMoney(maleRetail + femaleRetail) : ''}</td>
      <td style={{ padding: '7px 10px' }}>
        <button
          className="btn btn-primary"
          style={{ fontSize: 10, padding: '3px 8px' }}
          onClick={e => { e.stopPropagation(); onSelect && onSelect(); }}
        >
          View
        </button>
      </td>
    </tr>
  );
}

// ─── Detail View ──────────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-header">{title}</div>
      <div style={{ padding: '12px 16px' }}>{children}</div>
    </div>
  );
}

function LabelValue({ label, value, wide, highlight }) {
  return (
    <div style={{ minWidth: wide ? 160 : 120 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: highlight ? 700 : 400, color: highlight ? 'var(--navy)' : 'inherit' }}>{value}</div>
    </div>
  );
}

function VendorHistory({ crabberName, currentYieldPct, currentRatio, currentMargin }) {
  const [history, setHistory] = useState(null);
  const [days, setDays] = useState(30);
  const [activeTip, setActiveTip] = useState(null);

  useEffect(() => {
    if (!crabberName) return;
    setHistory(null);
    const end = new Date();
    const start = new Date(); start.setDate(start.getDate() - days);
    const fmt = d => `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${d.getFullYear()}`;
    fetchYieldSheetsByVendorHistory(fmt(start), fmt(end))
      .then(pos => {
        const vendor = pos.filter(p => {
          const name = p.crabberName ?? p.CrabberName ?? '';
          const dateStr = p.dateReceived ?? p.DateReceived;
          const date = dateStr ? new Date(dateStr) : null;
          const ty = Number(p.totalYield || 0);
          return name === crabberName && ty > 0 && (!date || (date >= start && date <= end));
        });
        setHistory(vendor);
      })
      .catch(() => setHistory([]));
  }, [crabberName, days]);

  if (!crabberName) return null;

  const sessions = history ?? [];
  const count = sessions.length;

  const { totalCrabsAll, totalYieldAll, totalCostAll, totalRetailAll } = sessions.reduce((s, p) => {
    s.totalCrabsAll += Number(p.totalCrabCount || 0) || Number(p.totalCrabs || 0);
    s.totalYieldAll += Number(p.totalYield || 0);
    s.totalCostAll  += Number(p.totalAmount || 0);
    s.totalRetailAll += Number(p.totalMaleRetailPrice || 0) + Number(p.totalFemaleRetailPrice || 0);
    return s;
  }, { totalCrabsAll: 0, totalYieldAll: 0, totalCostAll: 0, totalRetailAll: 0 });

  const avgYield = totalCrabsAll > 0 ? (totalYieldAll / totalCrabsAll) * 100 : null;
  const avgRatio = totalCostAll  > 0 ? (totalRetailAll / totalCostAll - 1) * 100 : null;

  const yieldDelta = avgYield !== null && currentYieldPct > 0 ? currentYieldPct - avgYield : null;
  const ratioDelta = avgRatio !== null && currentRatio > 0 ? currentRatio - avgRatio : null;

  const verdict = yieldDelta === null ? null
    : yieldDelta >= 3 ? { label: 'Above Average', color: '#16a34a', bg: '#dcfce7' }
    : yieldDelta <= -3 ? { label: 'Below Average', color: '#dc2626', bg: '#fee2e2' }
    : { label: 'On Average', color: '#d97706', bg: '#fef9c3' };

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Vendor History — {crabberName}</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {[30, 60, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700,
                background: days === d ? 'var(--navy)' : 'var(--gray-200)',
                color: days === d ? '#fff' : 'var(--gray-600)' }}>
              {d}d
            </button>
          ))}
        </div>
      </div>
      <div style={{ padding: '12px 16px' }}>
        {history === null ? (
          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Loading history...</div>
        ) : count === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>No prior trips in the last {days} days.</div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
                    {[
                      { label: 'Date' },
                      { label: 'Crabs' },
                      { label: 'Yield' },
                      { label: 'Yield %', tip: 'red < 90%  ·  black 90–100%  ·  green > 100%' },
                      { label: 'Cost' },
                      { label: 'Retail' },
                      { label: 'Ratio', tip: 'red < 80%  ·  black 80–110%  ·  green > 110%' },
                    ].map(({ label, tip }) => (
                      <th key={label} style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 600, fontSize: 10, color: 'var(--gray-500)', textTransform: 'uppercase', position: 'relative' }}
                        onMouseEnter={() => tip && setActiveTip(label)}
                        onMouseLeave={() => setActiveTip(null)}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          {label}
                          {tip && <Info size={10} style={{ color: 'var(--gray-400)', cursor: 'help', flexShrink: 0 }} />}
                        </span>
                        {tip && activeTip === label && (
                          <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 100, background: '#1e293b', color: '#fff', fontSize: 10, fontWeight: 400, padding: '5px 9px', borderRadius: 5, whiteSpace: 'nowrap', pointerEvents: 'none', marginTop: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.25)', textTransform: 'none', letterSpacing: 0 }}>
                            {tip}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((p, i) => {
                    const tc = Number(p.totalCrabCount || 0) || Number(p.totalCrabs || 0);
                    const ty = Number(p.totalYield || 0);
                    const yp = tc > 0 ? (ty / tc) * 100 : 0;
                    const cost = Number(p.totalAmount || 0);
                    const retail = Number(p.totalMaleRetailPrice || 0) + Number(p.totalFemaleRetailPrice || 0);
                    const ratio = cost > 0 ? (retail / cost - 1) * 100 : 0;
                    const yieldColor = yp === 0 ? 'inherit' : yp < 90 ? '#dc2626' : yp > 100 ? '#16a34a' : 'inherit';
                    const ratioColor = ratio === 0 ? 'inherit' : ratio < 80 ? '#dc2626' : ratio > 110 ? '#16a34a' : 'inherit';
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 1 ? '#f9fafb' : 'white' }}>
                        <td style={{ padding: '5px 8px' }}>{fmtDateShort(p.dateReceived ?? p.DateReceived)}</td>
                        <td style={{ padding: '5px 8px' }}>{tc > 0 ? `${fmtNum(tc, 0)} ${p.unitsOfMeasure || 'BU'}` : '—'}</td>
                        <td style={{ padding: '5px 8px' }}>{ty > 0 ? `${fmtNum(ty, 2)} BU` : '—'}</td>
                        <td style={{ padding: '5px 8px', fontWeight: 700, color: yieldColor }}>{yp > 0 ? fmtPct(yp) : '—'}</td>
                        <td style={{ padding: '5px 8px' }}>{fmtMoney(cost)}</td>
                        <td style={{ padding: '5px 8px' }}>{fmtMoney(retail)}</td>
                        <td style={{ padding: '5px 8px', fontWeight: 700, color: ratioColor }}>{ratio !== 0 ? fmtPct(ratio) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--gray-300)', background: '#f0f4f0', fontWeight: 700 }}>
                    <td style={{ padding: '6px 8px', fontSize: 11 }}>{count} trips</td>
                    <td style={{ padding: '6px 8px' }}>{totalCrabsAll > 0 ? `${fmtNum(totalCrabsAll, 0)} BU` : '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{totalYieldAll > 0 ? `${fmtNum(totalYieldAll, 2)} BU` : '—'}</td>
                    <td style={{ padding: '6px 8px', fontWeight: 700, color: avgYield === null ? 'inherit' : avgYield < 90 ? '#dc2626' : avgYield > 100 ? '#16a34a' : 'inherit' }}>
                      {avgYield !== null ? fmtPct(avgYield) : '—'}
                    </td>
                    <td style={{ padding: '6px 8px' }}>{fmtMoney(totalCostAll)}</td>
                    <td style={{ padding: '6px 8px' }}>{fmtMoney(totalRetailAll)}</td>
                    <td style={{ padding: '6px 8px', fontWeight: 700, color: avgRatio === null ? 'inherit' : avgRatio < 80 ? '#dc2626' : avgRatio > 110 ? '#16a34a' : 'inherit' }}>
                      {avgRatio !== null ? fmtPct(avgRatio) : '—'}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function YieldDetail({ detail, loading, onBack, crabberName }) {
  if (loading) {
    return (
      <div>
        <button className="btn" onClick={onBack} style={{ marginBottom: 16, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
          <ChevronLeft size={14} /> Back
        </button>
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>Loading...</div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div>
        <button className="btn" onClick={onBack} style={{ marginBottom: 16, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
          <ChevronLeft size={14} /> Back
        </button>
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>Failed to load detail.</div>
      </div>
    );
  }

  const po = detail.purchaseOrder ?? detail.PurchaseOrder ?? {};
  const poItems = detail.purchaseOrderItems ?? detail.PurchaseOrderItems ?? [];
  const transport = detail.purchaseOrderTransportation ?? detail.PurchaseOrderTransportation ?? {};
  const ys = detail.yieldSheet ?? detail.YieldSheet ?? {};
  const ysItems = detail.yieldSheetItems ?? detail.YieldSheetItems ?? [];
  const ysSummary = detail.yieldSheetSummary ?? detail.YieldSheetSummary ?? [];

  const maleItems = ysItems.filter(i => (i.gradedType ?? i.GradedType ?? '').toLowerCase() === 'male');
  const femaleItems = ysItems.filter(i => (i.gradedType ?? i.GradedType ?? '').toLowerCase() === 'female');

  const cost = Number(po.totalAmount || 0) + Number(transport.transportationCost || 0);
  // Retail = (tally + live) x retail_price — matches the Vendor Summary list and
  // the live-admin Yield PDF, which both count live crabs toward retail.
  const maleRetail = maleItems.reduce((s, i) => {
    const qty = (parseFloat(i.tally ?? i.Tally ?? 0) || 0) + (Number(i.live ?? i.Live ?? 0) || 0);
    return s + qty * Number(i.retailPrice ?? i.RetailPrice ?? 0);
  }, 0);
  const femaleRetail = femaleItems.reduce((s, i) => {
    const qty = (parseFloat(i.tally ?? i.Tally ?? 0) || 0) + (Number(i.live ?? i.Live ?? 0) || 0);
    return s + qty * Number(i.retailPrice ?? i.RetailPrice ?? 0);
  }, 0);
  const totalRetail = maleRetail + femaleRetail;
  const profit = totalRetail - cost;
  const margin = totalRetail > 0 ? (profit / totalRetail) * 100 : 0;

  const totalCrabs = Number(po.totalCrabs || ys.totalYield || 0);
  const dead = Number(ys.dead ?? 0);
  const totalYield = Number(ys.totalYield ?? po.totalYield ?? 0);
  // bushelDenominator: sum box+bushel qty per item (same logic as totalCrabCount in list view)
  const bushelDenominator = poItems.length > 0
    ? poItems.reduce((s, i) => s + Number(i.boxQuantity ?? i.BoxQuantity ?? 0) + Number(i.bushelQuantity ?? i.BushelQuantity ?? 0), 0)
    : totalCrabs;
  const yieldPct = bushelDenominator > 0 ? (totalYield / bushelDenominator) * 100 : 0;

  return (
    <div>
      <button className="btn" onClick={onBack} style={{ marginBottom: 16, fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
        <ChevronLeft size={14} /> Back to List
      </button>

      {/* Header */}
      <Section title="Purchase Order">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
          <LabelValue label="Date Received" value={fmtDate(po.dateReceived ?? po.DateReceived)} highlight />
          <LabelValue label="Date Processed" value={fmtDate(po.dateProcessed ?? po.DateProcessed)} />
          <LabelValue label="Invoice #" value={po.invoiceNumber || po.InvoiceNumber || '—'} />
          <LabelValue label="Invoice Date" value={fmtDate(po.invoiceDate ?? po.InvoiceDate)} />
          <LabelValue label="Total Crabs" value={fmtUnit(po.totalCrabs ?? po.TotalCrabs, po.unitOfMeasureId === 2 ? 'BU' : 'LBS')} highlight />
          <LabelValue label="Invoice Amount" value={fmtMoney(po.invoiceAmount ?? po.InvoiceAmount)} />
          <LabelValue label="Total Amount" value={fmtMoney(po.totalAmount ?? po.TotalAmount)} />
          {po.note && <LabelValue label="Note" value={po.note} wide />}
        </div>
      </Section>

      {/* PO Items */}
      {poItems.length > 0 && (
        <Section title="Purchased Items">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
                  {['Grade', 'Boxes', 'Weight (lbs)', 'Bushels', 'Price/Unit', 'Transport', 'Total Price'].map(h => (
                    <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, fontSize: 10, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {poItems.map((item, i) => {
                  const desc = item.description ?? item.Description;
                  const gradeLabel = WHOLESALE_LABELS[desc] || `Grade ${desc}`;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 1 ? 'var(--gray-50, #f9fafb)' : 'white' }}>
                      <td style={{ padding: '7px 10px', fontWeight: 600 }}>{gradeLabel}</td>
                      <td style={{ padding: '7px 10px' }}>{item.boxQuantity ?? item.BoxQuantity ?? '—'}</td>
                      <td style={{ padding: '7px 10px' }}>{fmtNum(item.weight ?? item.Weight, 1)}</td>
                      <td style={{ padding: '7px 10px' }}>{item.bushelQuantity ?? item.BushelQuantity ?? '—'}</td>
                      <td style={{ padding: '7px 10px' }}>{fmtMoney(item.price ?? item.Price)}/bu</td>
                      <td style={{ padding: '7px 10px' }}>{fmtMoney(item.itemTransportationCost ?? item.ItemTransportationCost)}</td>
                      <td style={{ padding: '7px 10px', fontWeight: 600 }}>{fmtMoney(item.totalPrice ?? item.TotalPrice)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {transport.transportationCost > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--gray-500)' }}>
              Transportation: <strong>{fmtMoney(transport.transportationCost ?? transport.TransportationCost)}</strong>
              {transport.transportationInvoiceDate && ` (Invoice: ${fmtDate(transport.transportationInvoiceDate)})`}
            </div>
          )}
        </Section>
      )}

      {/* Yield Sheet */}
      {(ys.yieldSheetId || ys.YieldSheetId) ? (
        <Section title="Yield Sheet">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginBottom: 16 }}>
            <LabelValue label="Date Cooked" value={fmtDate(ys.dateCooked ?? ys.DateCooked)} />
            <LabelValue label="Dead" value={fmtUnit(ys.dead ?? ys.Dead, 'BU')} highlight />
            <LabelValue label="Total Yield" value={fmtUnit(ys.totalYield ?? ys.TotalYield, 'BU')} highlight />
            <LabelValue label="Male Yield" value={fmtUnit(ys.totalMaleYielded ?? ys.TotalMaleYielded, 'BU')} />
            <LabelValue label="Female Yield" value={fmtUnit(ys.totalFemaleYielded ?? ys.TotalFemaleYielded, 'BU')} />
            {totalCrabs > 0 && dead > 0 && <LabelValue label="Dead %" value={fmtPct((dead / totalCrabs) * 100)} />}
            {yieldPct > 0 && <LabelValue label="Yield %" value={fmtPct(yieldPct)} highlight />}
          </div>

          {/* Grade breakdown */}
          {ysItems.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: maleItems.length > 0 && femaleItems.length > 0 ? '1fr 1fr' : '1fr', gap: 16 }}>
              {[{ label: 'Male Grades', items: maleItems }, { label: 'Female Grades', items: femaleItems }]
                .filter(g => g.items.length > 0)
                .map(({ label, items }) => (
                  <div key={label}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>{label}</div>
                    <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
                          {['Grade', 'Tally', 'Live (lbs)', 'Retail Price', 'Value'].map(h => (
                            <th key={h} style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 600, fontSize: 10, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, i) => {
                          const tally = parseFloat(item.tally ?? item.Tally ?? 0) || 0;
                          const live = Number(item.live ?? item.Live ?? 0) || 0;
                          const price = Number(item.retailPrice ?? item.RetailPrice ?? 0);
                          const value = (tally + live) * price;
                          return (
                            <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 1 ? 'var(--gray-50, #f9fafb)' : 'white' }}>
                              <td style={{ padding: '6px 8px', fontWeight: 600 }}>{item.gradedName ?? item.GradedName ?? `Grade ${item.gradedId ?? item.GradedId}`}</td>
                              <td style={{ padding: '6px 8px' }}>{tally > 0 ? fmtNum(tally, 0) : '—'}</td>
                              <td style={{ padding: '6px 8px' }}>{Number(item.live ?? item.Live ?? 0) > 0 ? fmtLbs(item.live ?? item.Live) : '—'}</td>
                              <td style={{ padding: '6px 8px' }}>{fmtMoney(price)}</td>
                              <td style={{ padding: '6px 8px', fontWeight: 600 }}>{value > 0 ? fmtMoney(value) : '—'}</td>
                            </tr>
                          );
                        })}
                        <tr style={{ borderTop: '2px solid var(--gray-200)', fontWeight: 700 }}>
                          <td colSpan={4} style={{ padding: '6px 8px', textAlign: 'right', fontSize: 11, color: 'var(--gray-500)' }}>Total Retail Value</td>
                          <td style={{ padding: '6px 8px', color: 'var(--navy)' }}>
                            {fmtMoney(items.reduce((s, item) => {
                              const qty = (parseFloat(item.tally ?? item.Tally ?? 0) || 0) + (Number(item.live ?? item.Live ?? 0) || 0);
                              return s + qty * Number(item.retailPrice ?? item.RetailPrice ?? 0);
                            }, 0))}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}
            </div>
          )}
        </Section>
      ) : null}

      {/* Yield Summary by wholesale grade */}
      {ysSummary.length > 0 && (
        <Section title="Wholesale Grade Summary">
          <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse', maxWidth: 500 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
                {['Grade', 'Total (BU)', 'Dead (BU)', 'Held for Live (BU)'].map(h => (
                  <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, fontSize: 10, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ysSummary.map((row, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 1 ? 'var(--gray-50, #f9fafb)' : 'white' }}>
                  <td style={{ padding: '7px 10px', fontWeight: 600 }}>{row.typeName ?? row.TypeName ?? WHOLESALE_LABELS[row.typeId ?? row.TypeId] ?? `Type ${row.typeId ?? row.TypeId}`}</td>
                  <td style={{ padding: '7px 10px' }}>{fmtUnit(row.total ?? row.Total, 'BU')}</td>
                  <td style={{ padding: '7px 10px', color: '#dc2626' }}>{Number(row.dead ?? row.Dead ?? 0) > 0 ? fmtUnit(row.dead ?? row.Dead, 'BU') : '—'}</td>
                  <td style={{ padding: '7px 10px' }}>{Number(row.heldForLive ?? row.HeldForLive ?? 0) > 0 ? fmtUnit(row.heldForLive ?? row.HeldForLive, 'BU') : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* Vendor History */}
      <VendorHistory
        crabberName={crabberName}
        currentYieldPct={yieldPct}
        currentRatio={(() => {
          const r = Number(po.totalMaleRetailPrice ?? po.TotalMaleRetailPrice ?? 0) + Number(po.totalFemaleRetailPrice ?? po.TotalFemaleRetailPrice ?? 0);
          const c = Number(po.totalAmount ?? po.TotalAmount ?? 0);
          return r > 0 && c > 0 ? (r / c - 1) * 100 : 0;
        })()}
        currentMargin={margin}
      />

      {/* Financial Summary */}
      <Section title="Financial Summary">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
          {[
            { label: 'Invoice Amount', value: fmtMoney(po.invoiceAmount ?? po.InvoiceAmount) },
            { label: 'Transport Cost', value: fmtMoney(transport.transportationCost ?? transport.TransportationCost) },
            { label: 'Total Cost', value: fmtMoney(cost), highlight: true },
            { label: 'Male Retail', value: fmtMoney(maleRetail) },
            { label: 'Female Retail', value: fmtMoney(femaleRetail) },
            { label: 'Total Retail Value', value: fmtMoney(totalRetail), highlight: true },
            { label: 'Gross Profit', value: fmtMoney(profit), highlight: true },
            { label: 'Margin', value: fmtPct(margin), highlight: true },
          ].map(({ label, value, highlight }) => (
            <div key={label} style={{ background: 'var(--gray-50, #f9fafb)', borderRadius: 6, padding: '10px 12px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: highlight ? (label === 'Gross Profit' && profit < 0 ? '#dc2626' : 'var(--navy)') : 'inherit' }}>{value}</div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function YieldReport() {
  const [startDate, setStartDate] = useState(isoMonthStart());
  const [endDate, setEndDate] = useState(isoToday());
  const [groups, setGroups] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;

  // expanded group state: { dateStr -> { pos, loading } }
  const [expanded, setExpanded] = useState({});

  // detail view
  const [detailId, setDetailId] = useState(null);
  const [detailCrabberName, setDetailCrabberName] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = (s, e, p = 1) => {
    setLoading(true);
    setError(null);
    setExpanded({});
    fetchYieldSheetsByDateRange(toApiDate(s), toApiDate(e), p, PAGE_SIZE)
      .then(({ rows, totalRows: t }) => {
        setGroups(rows);
        setTotalRows(t);
        setPage(p);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(startDate, endDate, 1); }, []);

  const toggleGroup = (dateStr) => {
    if (expanded[dateStr]) {
      setExpanded(prev => { const n = { ...prev }; delete n[dateStr]; return n; });
      return;
    }
    setExpanded(prev => ({ ...prev, [dateStr]: { pos: [], loading: true } }));
    // Normalize whatever text format the DB stored — Neon holds date_received as
    // '2026-08-11 00:00:00' (space, no 'T'), the API can send ISO with 'T' — down
    // to the MM/DD/YYYY that fetchYieldSheetList expects. String-slice instead of
    // new Date() so a bare date can never shift a day across timezones.
    let apiDate = dateStr;
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateStr || ''));
    if (iso) apiDate = `${iso[2]}/${iso[3]}/${iso[1]}`;
    fetchYieldSheetList(apiDate)
      .then(pos => setExpanded(prev => ({ ...prev, [dateStr]: { pos, loading: false } })))
      .catch(() => setExpanded(prev => ({ ...prev, [dateStr]: { pos: [], loading: false } })));
  };

  const openDetail = (id, crabberName) => {
    setDetailId(id);
    setDetailCrabberName(crabberName || null);
    setDetail(null);
    setDetailLoading(true);
    fetchPurchaseOrderById(id)
      .then(setDetail)
      .catch(() => {})
      .finally(() => setDetailLoading(false));
  };

  if (detailId !== null) {
    return <YieldDetail detail={detail} loading={detailLoading} crabberName={detailCrabberName} onBack={() => { setDetailId(null); setDetail(null); setDetailCrabberName(null); }} />;
  }

  const totalPages = Math.ceil(totalRows / PAGE_SIZE);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Yield Report</h2>
      </div>

      {/* Date filter */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Date Range</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>FROM</div>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="form-input" style={{ fontSize: 11 }} />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>TO</div>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="form-input" style={{ fontSize: 11 }} />
          </div>
          <button onClick={() => load(startDate, endDate, 1)} className="btn btn-primary" style={{ fontSize: 11 }}>Search</button>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {[30, 60, 90].map(d => (
              <button key={d} onClick={() => {
                const end = isoToday();
                const s = new Date(); s.setDate(s.getDate() - d);
                const start = s.toISOString().split('T')[0];
                setStartDate(start);
                setEndDate(end);
                load(start, end, 1);
              }}
                style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, background: 'var(--gray-200)', color: 'var(--gray-600)' }}>
                {d}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>Loading...</div>}
      {error && <div className="card" style={{ padding: 16, color: '#dc2626', fontSize: 12 }}>{error}</div>}

      {!loading && !error && groups.length === 0 && (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
          No purchase orders found for this date range.
        </div>
      )}

      {groups.length > 0 && (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Purchase Orders by Date ({totalRows} total)</span>
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                <button className="btn" style={{ padding: '3px 8px', fontSize: 11 }} disabled={page <= 1} onClick={() => load(startDate, endDate, page - 1)}>
                  <ChevronLeft size={13} />
                </button>
                <span style={{ color: 'var(--gray-500)' }}>Page {page} of {totalPages}</span>
                <button className="btn" style={{ padding: '3px 8px', fontSize: 11 }} disabled={page >= totalPages} onClick={() => load(startDate, endDate, page + 1)}>
                  <ChevronRight size={13} />
                </button>
              </div>
            )}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#c8d8b8', borderBottom: '2px solid #b0c898' }}>
                  {['Date Received', 'Total Yield Sheet(s)', 'Finalized Yield Sheet(s)', ''].map(h => (
                    <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 700, fontSize: 12, color: '#333' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map(group => {
                  const key = group.dateReceived ?? group.DateReceived;
                  const exp = expanded[key];
                  return (
                    <DateGroupRow
                      key={key}
                      group={{ ...group, dateReceived: group.dateReceived ?? group.DateReceived, finalizeCount: group.finalizeCount ?? group.FinalizeCount ?? 0, nonFinalizeCount: group.nonFinalizeCount ?? group.NonFinalizeCount ?? 0, totalCount: group.totalCount ?? group.TotalCount ?? 0 }}
                      isExpanded={!!exp}
                      onSelect={() => toggleGroup(key)}
                      poList={exp?.pos}
                      poLoading={exp?.loading}
                      onOpenDetail={openDetail}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
